import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, RecBadge, EmptyState, LoadingRows, ErrorBox, Stat } from "@/components/common";
import { AssistantPanel } from "@/components/common/AssistantPanel";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtDateTime, isOverdue, titleCase } from "@/lib/format";
import { Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

function TaskRow({ t, onDone, onOpen }) {
  return (
    <tr onClick={() => t.account_id && onOpen(t.account_id)} data-testid={`today-task-${t.id}`} className={isOverdue(t.due_at) ? "border-l-2 border-l-[hsl(var(--warning))]" : ""}>
      <td className="font-medium">{t.title}<div className="meta">{t.account_name || "No account"} · {titleCase(t.type)}</div></td>
      <td><RecBadge value={t.account_recommendation} /></td>
      <td className={`nums ${isOverdue(t.due_at) ? "text-[hsl(var(--warning))] font-medium" : "text-muted-foreground"}`}>{t.due_at ? fmtDateTime(t.due_at) : "Unscheduled"}</td>
      <td className="text-muted-foreground">{t.assignee_name}</td>
      <td className="text-right" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="ghost" onClick={() => onDone(t)} data-testid={`today-task-done-${t.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Done</Button>
      </td>
    </tr>
  );
}

export default function TodayPage() {
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [assist, setAssist] = useState(false);

  const load = useCallback(() => api.get("/today").then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const markDone = async (t) => {
    try { await api.patch(`/tasks/${t.id}`, { status: "done" }); toast.success("Task completed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const open = (id) => navigate(`/accounts?open=${id}`);

  if (error) return <div className="page"><ErrorBox error={error} onRetry={load} /></div>;
  if (!data) return <div className="page"><LoadingRows rows={8} /></div>;

  const tasks = [...data.overdue, ...data.due_today];
  const nothing = !tasks.length && !data.contact_now.length && !data.research_first.length && !data.new_candidates && !data.upcoming.length;

  return (
    <div className="page">
      <PageHeader title={`Today, ${dayjs().format("dddd D MMMM")}`} subtitle={`Good ${dayjs().hour() < 12 ? "morning" : "afternoon"}, ${me?.user?.name?.split(" ")[0]}. Here is what needs your attention, in priority order.`}
        actions={can("ai.use") && <Button variant="outline" onClick={() => setAssist(true)} data-testid="today-assistant-button"><Sparkles className="h-4 w-4 mr-2" />Prepare my day</Button>} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Overdue" value={data.overdue.length} testId="stat-overdue" />
        <Stat label="Due today" value={data.due_today.length} testId="stat-due-today" />
        <Stat label="Contact now" value={data.contact_now.length} testId="stat-contact-now" />
        <Stat label="New prospects to review" value={data.new_candidates} testId="stat-new-candidates" />
        <Stat label="Research running" value={data.running_research} testId="stat-running" />
      </div>

      {nothing && <EmptyState title="Nothing scheduled yet" text="Start by running discovery for one of your ICPs, or accept candidates into accounts and schedule the first action." action={<Button onClick={() => navigate("/discover")}>Go to Discover <ArrowRight className="h-4 w-4 ml-1" /></Button>} testId="today-empty" />}

      {tasks.length > 0 && (
        <section className="space-y-2">
          <h2 className="section-title">Follow-ups due</h2>
          <div className="table-wrap"><table className="w-full table-dense" data-testid="today-tasks-table">
            <thead><tr><th>Action</th><th>Account signal</th><th>Due</th><th>Owner</th><th></th></tr></thead>
            <tbody>{tasks.map((t) => <TaskRow key={t.id} t={t} onDone={markDone} onOpen={open} />)}</tbody>
          </table></div>
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {data.contact_now.length > 0 && (
          <section className="space-y-2">
            <h2 className="section-title">Ready to contact</h2>
            <div className="table-wrap"><table className="w-full table-dense" data-testid="today-contact-now-table">
              <thead><tr><th>Company</th><th>Next action</th><th>Owner</th></tr></thead>
              <tbody>{data.contact_now.map((a) => (
                <tr key={a.id} onClick={() => open(a.id)} data-testid={`today-account-${a.id}`}>
                  <td className="font-medium">{a.name}<div className="mt-0.5"><RecBadge value={a.assessment?.recommendation} /></div></td>
                  <td className="text-muted-foreground">{a.next_action_text || "—"}{a.next_action_due && <div className="meta">{fmtDate(a.next_action_due)}</div>}</td>
                  <td className="text-muted-foreground">{a.owner_name || <span className="chip chip-warning">Unassigned</span>}</td>
                </tr>))}</tbody>
            </table></div>
          </section>
        )}
        {data.research_first.length > 0 && (
          <section className="space-y-2">
            <h2 className="section-title">Research first</h2>
            <div className="table-wrap"><table className="w-full table-dense">
              <thead><tr><th>Company</th><th>Why</th></tr></thead>
              <tbody>{data.research_first.map((a) => (
                <tr key={a.id} onClick={() => open(a.id)}>
                  <td className="font-medium">{a.name}</td>
                  <td className="text-muted-foreground text-xs">{a.assessment?.explanation}</td>
                </tr>))}</tbody>
            </table></div>
          </section>
        )}
      </div>

      {(data.opportunities_without_next_action.length > 0 || data.unassigned_accounts.length > 0 || data.recent_replies.length > 0) && (
        <div className="grid lg:grid-cols-3 gap-4">
          {data.opportunities_without_next_action.length > 0 && (
            <section className="panel p-4 space-y-2" data-testid="today-opps-no-next">
              <h2 className="section-title text-[hsl(var(--warning))]">Opportunities without a next action</h2>
              {data.opportunities_without_next_action.map((o) => <button key={o.id} className="block text-sm hover:underline text-left" onClick={() => navigate(`/pipeline?open=${o.id}`)}>{o.name} <span className="meta">· {titleCase(o.stage)}</span></button>)}
            </section>
          )}
          {data.unassigned_accounts.length > 0 && (
            <section className="panel p-4 space-y-2" data-testid="today-unassigned">
              <h2 className="section-title">Manager review queue (unassigned)</h2>
              {data.unassigned_accounts.map((a) => <button key={a.id} className="block text-sm hover:underline text-left" onClick={() => open(a.id)}>{a.name}</button>)}
            </section>
          )}
          {data.recent_replies.length > 0 && (
            <section className="panel p-4 space-y-2">
              <h2 className="section-title">Replies this week</h2>
              {data.recent_replies.map((r) => <button key={r.id} className="block text-sm text-left hover:underline" onClick={() => open(r.account_id)}>{r.text.slice(0, 90)}</button>)}
            </section>
          )}
        </div>
      )}

      {data.upcoming.length > 0 && (
        <section className="space-y-2">
          <h2 className="section-title">Coming up this week</h2>
          <div className="table-wrap"><table className="w-full table-dense">
            <thead><tr><th>Action</th><th>Account signal</th><th>Due</th><th>Owner</th><th></th></tr></thead>
            <tbody>{data.upcoming.map((t) => <TaskRow key={t.id} t={t} onDone={markDone} onOpen={open} />)}</tbody>
          </table></div>
        </section>
      )}

      <AssistantPanel open={assist} onOpenChange={setAssist} contextType="today" title="Prepare my day" suggestions={["Which account should I contact first and why?", "Summarise my overdue follow-ups and suggest an order.", "What is missing evidence before I call anyone?"]} />
    </div>
  );
}
